// "While you were out": a short handover of what happened since a moment
// (someone got home, or opened the app after a while), and what's next.
// Pure; used by Today, the phone alert on arrival and the voice assistant.

import { careState, openCare } from './care.ts';
import { LEVEL_LABEL, healthWatch } from './healthwatch.ts';
import { sleepStatus, toiletStatus } from './status.ts';
import { formatDuration, formatTime } from './time.ts';
import type { CareItem, Household, LogEvent, Millis, Profile, VetVisit } from './types.ts';
import { ageNow } from './age.ts';
import { mealPlan } from './feeding.ts';

export type HandoverKind = 'toilet' | 'meal' | 'sleep' | 'walk' | 'alone' | 'health' | 'note' | 'camera' | 'care' | 'training' | 'next' | 'flag';

export interface HandoverLine {
  kind: HandoverKind;
  text: string;
  /** Serious enough to stand out. */
  alert?: boolean;
}

export interface Handover {
  since: Millis;
  /** e.g. "While you were out · 3h 20m" */
  title: string;
  /** What happened, most important first. */
  lines: HandoverLine[];
  /** What's coming up. */
  next: HandoverLine[];
  /** How many things were logged in the window. */
  logged: number;
  /** One sentence for a phone notification. */
  summary: string;
}

export interface HandoverInput {
  events: LogEvent[];
  profile?: Profile;
  household?: Household;
  care: CareItem[];
  since: Millis;
  /** End of the time away (defaults to now) — what's next is always worked out for now. */
  until?: Millis;
  now: Millis;
  visits?: VetVisit[];
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function handover({ events, profile, household, care, since, until: untilIn, now, visits }: HandoverInput): Handover {
  const until = Math.min(untilIn ?? now, now);
  const name = profile?.name || 'Arlo';
  const people = household?.people ?? [];
  const who = (id?: string) => (id ? (people.find((p) => p.id === id)?.name ?? '') : '');
  const inWindow = events.filter((e) => e.at > since && e.at <= until).sort((a, b) => a.at - b.at);
  const lines: HandoverLine[] = [];

  // Toilet
  const wees = inWindow.filter((e): e is LogEvent<'wee'> => e.type === 'wee');
  const poos = inWindow.filter((e): e is LogEvent<'poo'> => e.type === 'poo');
  const inside = [...wees, ...poos].filter((e) => e.data.where === 'inside');
  if (wees.length || poos.length) {
    const lastWee = wees.at(-1);
    const bits = [wees.length ? plural(wees.length, 'wee') : '', poos.length ? plural(poos.length, 'poo') : ''].filter(Boolean).join(' and ');
    const loose = poos.filter((p) => p.data.quality === 'runny' || p.data.quality === 'soft');
    lines.push({
      kind: 'toilet',
      text: `${bits}${inside.length ? ` (${plural(inside.length, 'accident')} indoors)` : ', all outside'}${lastWee ? ` — last wee ${formatTime(lastWee.at)}${who(lastWee.by) ? ` with ${who(lastWee.by)}` : ''}` : ''}.${loose.length ? ` ${loose.length === 1 ? 'One poo was' : `${loose.length} poos were`} ${loose.some((p) => p.data.quality === 'runny') ? 'runny' : 'soft'}.` : ''}`,
      alert: inside.length > 1 || loose.some((p) => p.data.quality === 'runny'),
    });
  }

  // Meals
  const meals = inWindow.filter((e): e is LogEvent<'meal'> => e.type === 'meal');
  if (meals.length) {
    const off = meals.filter((m) => m.data.appetite === 'none' || m.data.appetite === 'some');
    lines.push({ kind: 'meal', text: `${meals.length === 1 ? 'Ate at' : `${meals.length} meals, last at`} ${formatTime(meals.at(-1)!.at)}${off.length ? ` — didn’t finish ${off.length === 1 ? 'one' : off.length}` : ''}.`, alert: off.some((m) => m.data.appetite === 'none') });
  }

  // Sleep
  const age = profile ? ageNow(profile, now) : undefined;
  const weeks = age?.weeks ?? 12;
  const sleep = sleepStatus(events, weeks, now);
  const naps = inWindow.filter((e) => e.type === 'sleep' && e.end !== undefined);
  const napMs = naps.reduce((t, e) => t + ((e.end ?? e.at) - e.at), 0);
  if (sleep.asleep && sleep.current) lines.push({ kind: 'sleep', text: `Asleep now — since ${formatTime(sleep.current.at)} (${formatDuration(now - sleep.current.at)}).` });
  else if (napMs > 20 * 60_000) lines.push({ kind: 'sleep', text: `Napped ${formatDuration(napMs)} in total.` });

  // Out and about, alone time, training
  const walks = inWindow.filter((e) => e.type === 'walk' && !(e.type === 'walk' && e.data.auto === 'pending'));
  if (walks.length) lines.push({ kind: 'walk', text: `${plural(walks.length, 'outing')}${walks.some((w) => who(w.by)) ? ` (${[...new Set(walks.map((w) => who(w.by)).filter(Boolean))].join(', ')})` : ''}.` });
  const alone = inWindow.filter((e) => e.type === 'alone');
  if (alone.length) {
    const ms = alone.reduce((t, e) => t + ((e.end ?? now) - e.at), 0);
    const barks = inWindow.filter((e) => e.type === 'camera' && (e.data.kind === 'bark' || e.data.kind === 'cry' || e.data.kind === 'sound'));
    lines.push({ kind: 'alone', text: `On his own for ${formatDuration(ms)}${barks.length ? ` — the camera heard ${plural(barks.length, 'bark or cry', 'barks or cries')}` : ', quietly'}.`, alert: barks.length > 3 });
  }
  const training = inWindow.filter((e) => e.type === 'training');
  if (training.length) lines.push({ kind: 'training', text: `${plural(training.length, 'training session')}.` });

  // Health and care
  for (const e of inWindow) {
    if (e.type === 'health') lines.push({ kind: 'health', text: `${formatTime(e.at)}: logged ${e.data.symptom.replace('-', ' ')}${e.note ? ` — “${e.note}”` : ''}.`, alert: true });
    if (e.type === 'meds') lines.push({ kind: 'health', text: `${formatTime(e.at)}: gave ${e.data.name}${e.data.dose ? ` (${e.data.dose})` : ''}.` });
    if (e.type === 'care') lines.push({ kind: 'care', text: `Done: ${e.data.title}${who(e.by) ? ` (${who(e.by)})` : ''}.` });
  }

  // Vet visits or calls while you were out.
  for (const v of visits ?? [])
    if (v.at > since && v.at <= until)
      lines.push({ kind: 'health', text: `${v.kind === 'call' ? 'Spoke to' : 'Saw'} ${v.practice ?? 'the vet'}${v.reason ? ` about ${v.reason.split(' — ')[0].toLowerCase()}` : ''}${v.notes?.trim() ? `: “${v.notes.trim().slice(0, 160)}${v.notes.trim().length > 160 ? '…' : ''}”` : '.'}` });

  // Notes left for each other — word for word.
  for (const e of inWindow) if (e.type === 'note' && e.note?.trim()) lines.push({ kind: 'note', text: `${who(e.by) || 'Note'}: “${e.note.trim()}”` });

  // Put anything worrying first.
  lines.sort((a, b) => Number(!!b.alert) - Number(!!a.alert));

  // What's next
  const next: HandoverLine[] = [];
  for (const f of healthWatch({ events, care, profile, now }).filter((f) => f.level !== 'watch')) next.push({ kind: 'flag', text: `${LEVEL_LABEL[f.level]}: ${f.title.toLowerCase()}.`, alert: true });
  if (profile && (!profile.homeDate || profile.homeDate <= new Date(now).toISOString().slice(0, 10))) {
    const t = toiletStatus(events, weeks, now, sleep, household?.home?.type === 'flat' ? (household.home.outsideMins ?? 3) : 0);
    if (!sleep.asleep && t.dueAt !== undefined && !t.stale) next.push({ kind: 'next', text: t.urgency === 'now' || t.urgency === 'overdue' ? 'Toilet trip due now.' : `Toilet trip in about ${formatDuration(t.dueAt - now, { long: true })}.`, alert: t.urgency === 'overdue' });
    else if (sleep.asleep) next.push({ kind: 'next', text: 'When he wakes: straight out for a wee.' });
    if (household) {
      const plan = mealPlan({ ageWeeks: weeks, food: profile.food, household, events, now });
      if (plan.next && plan.next.at > now - 20 * 60_000) next.push({ kind: 'next', text: `Next meal ${plan.next.time}${plan.next.grams ? ` (${plan.next.grams} g)` : ''}.` });
    }
  }
  const due = openCare(care).filter((c) => careState(c, now) === 'due' || careState(c, now) === 'overdue');
  if (due.length) next.push({ kind: 'next', text: `Still to do today: ${due.map((c) => c.title).join(', ')}.` });

  const away = formatDuration(until - since);
  const headline = lines[0]?.text ?? `Nothing logged — ${name} may just have been snoozing.`;
  return {
    since,
    title: `While you were out · ${away}`,
    lines,
    next,
    logged: inWindow.length,
    summary: [headline, next[0]?.text].filter(Boolean).join(' '),
  };
}
